"use client";

import { useMemo } from "react";

import { STADIUM_WAREHOUSE_ADDRESS, US_ISO } from "@stadium/constants";
import { useAuthQuery } from "@stadium/services/apollo-client";
import { useFlags } from "@stadium/services/launch-darkly";
import { threePlWarehouseUpdatesFlag } from "@stadium/services/launch-darkly/constants";

import {
  GetUserWarehouseAddressDocument,
  type GetUserWarehouseAddressQuery,
} from "@stadium/hooks/graphql/operations";

type WarehouseAddress = NonNullable<
  NonNullable<GetUserWarehouseAddressQuery["user"]>["warehouse"]
>["address"];

type UseGetUserWarehouseAddressOptions = {
  country?: string;
  skip?: boolean;
};

const formatWarehouseAddress = (address?: WarehouseAddress | null) => {
  if (!address) return "";

  const { formattedAddress, address1, address2, city, stateName, zipcode, phone } = address;

  if (formattedAddress) {
    return phone ? `${formattedAddress}. Phone: ${phone}` : formattedAddress;
  }

  const locationLine = [address1, address2, city].filter(Boolean).join(", ");
  const stateZip = [stateName, zipcode].filter(Boolean).join(" ");
  const addressLine = [locationLine, stateZip].filter(Boolean).join(", ");

  return phone ? `${addressLine}. Phone: ${phone}` : addressLine;
};

const getStaticWarehouseAddressStr = () => {
  const { line1, line2, state, zip, phoneNum } = STADIUM_WAREHOUSE_ADDRESS;

  return `${line1}, ${line2}, ${state} ${zip}. Phone: ${phoneNum}`;
};

const useGetUserWarehouseAddress = ({
  country = US_ISO,
  skip = false,
}: UseGetUserWarehouseAddressOptions = {}) => {
  const { [threePlWarehouseUpdatesFlag]: isThreePlWarehouseUpdatesEnabled } = useFlags();

  const { data, loading, refetch } = useAuthQuery(GetUserWarehouseAddressDocument, {
    variables: { country },
    fetchPolicy: "cache-and-network",
    skip: skip || !isThreePlWarehouseUpdatesEnabled,
  });

  const warehouseDetails = data?.user?.warehouse;

  const address = warehouseDetails?.address;

  const staticAddressStr = useMemo(() => getStaticWarehouseAddressStr(), []);

  const addressStr = useMemo(() => {
    if (!isThreePlWarehouseUpdatesEnabled) return staticAddressStr;

    return formatWarehouseAddress(address);
  }, [address, isThreePlWarehouseUpdatesEnabled, staticAddressStr]);

  return {
    warehouseDetails,
    address,
    addressStr,
    loading: isThreePlWarehouseUpdatesEnabled && loading,
    isThreePlWarehouseUpdatesEnabled,
    refetch,
  };
};

export default useGetUserWarehouseAddress;
